import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type { CommonVariables } from "../types";

// --- Constants ---
const CART_TOKEN_STORAGE_KEY = "wc_cart_token";

// --- Token Management ---

// Attempt to load Cart-Token from localStorage on initial load
let currentCartToken: string | null = localStorage.getItem(
  CART_TOKEN_STORAGE_KEY,
);

// This nonce is for authenticating with the WP REST API (for our custom endpoints)
let currentWpRestApiNonce: string | undefined = (
  window.commonVariables as CommonVariables | undefined
)?.wpRestApiNonce;

export const saveCartTokenToStorage = (token: string | null) => {
  if (token) {
    localStorage.setItem(CART_TOKEN_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(CART_TOKEN_STORAGE_KEY);
  }
};

export const updateCartToken = (newToken: string | null) => {
  if (newToken !== currentCartToken) {
    currentCartToken = newToken;
    saveCartTokenToStorage(currentCartToken);
  }
};

export const updateWpRestNonce = (newNonce: string | undefined) => {
  if (newNonce && newNonce !== currentWpRestApiNonce) {
    currentWpRestApiNonce = newNonce;
  }
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl:
    (window.commonVariables as CommonVariables | undefined)?.siteUrl || "",
  prepareHeaders: (headers) => {
    // 1. Add the Cart-Token header if it exists. This is used by the WC Store API.
    if (currentCartToken) {
      headers.set("Cart-Token", currentCartToken);
    }

    // 2. Add the WP REST API Nonce header if it exists.
    if (currentWpRestApiNonce) {
      headers.set("X-WP-Nonce", currentWpRestApiNonce);
    }

    return headers;
  },
  credentials: "include",
});

// --- Nonce Refresh Lock ---
// Prevents multiple requests that fail at the same time from spamming the refresh endpoint.
let nonceRefreshPromise: Promise<string | null> | null = null;

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // 1. Await the refresh lock if another request is currently refreshing the nonce
  if (nonceRefreshPromise) {
    await nonceRefreshPromise;
  }

  // --- B2B CACHE BUSTER EKLENTİSİ (URLSearchParams ve Düz Nesne Uyumlu) ---
  let modifiedArgs = args;
  const isApproved = window.commonVariables?.isApprovedMember ? "1" : "0";

  if (typeof args === "string") {
    const separator = args.includes("?") ? "&" : "?";
    modifiedArgs = `${args}${separator}b2b_auth=${isApproved}`;
  } else if (args && (!args.method || args.method.toUpperCase() === "GET")) {
    if (args.params instanceof URLSearchParams) {
      args.params.append("b2b_auth", isApproved);
      modifiedArgs = args;
    } else {
      modifiedArgs = {
        ...args,
        params: {
          ...(args.params || {}),
          b2b_auth: isApproved,
        },
      };
    }
  }
  // --- B2B CACHE BUSTER EKLENTİSİ SONU ---

  let result = await rawBaseQuery(modifiedArgs, api, extraOptions);

  if (result.meta?.response?.headers) {
    const headers = result.meta.response.headers;

    // Update Cart-Token (for WC Store API)
    const newCartToken = headers.get("Cart-Token");
    if (newCartToken) {
      updateCartToken(newCartToken);
    }

    // Update WP REST API Nonce
    const newWpRestNonce = headers.get("X-WP-Nonce");
    if (newWpRestNonce) {
      updateWpRestNonce(newWpRestNonce);
    }
  }

  // --- SMART ERROR HANDLING & NONCE REFRESH ---
  if (
    result.error &&
    typeof result.error === "object" &&
    "status" in result.error
  ) {
    const status = result.error.status;
    const urlString =
      typeof modifiedArgs === "string" ? modifiedArgs : modifiedArgs.url;

    // 1. Ignore auth errors from the login/register endpoints themselves.
    // These just mean "wrong password" or "user exists", we shouldn't destroy the cart!
    const isAuthEndpoint =
      urlString.includes("/user/login") || urlString.includes("/user/register");

    if (!isAuthEndpoint && (status === 401 || status === 403)) {
      // 2. Determine who threw the error.
      const isStoreApiError = urlString.includes("/wc/store/v1");

      if (isStoreApiError) {
        // WooCommerce Store API specifically rejected the cart token.
        console.warn(
          `[API] Store API Token expired or invalid. Resetting cart token.`,
        );
        saveCartTokenToStorage(null);
        currentCartToken = null;

        // Instead of nuking the entire app state, just force the cart to gracefully refetch empty
        api.dispatch(baseApi.util.invalidateTags(["Cart", "Checkout"]));
      } else {
        // A Custom WP REST API endpoint threw a 403 (likely a stale Nonce).
        console.warn(
          `[API] Custom REST API auth error (${status}). Attempting to refresh nonce...`,
        );

        if (!nonceRefreshPromise) {
          const ajaxUrl =
            (window.commonVariables as CommonVariables | undefined)?.ajaxUrl ||
            "/wp-admin/admin-ajax.php";

          nonceRefreshPromise = fetch(`${ajaxUrl}?action=refresh_rest_nonce`, {
            // Include credentials so WP knows who is asking (if logged in)
            credentials: "include",
          })
            .then((res) => res.json())
            .then((data) => {
              if (data.success && data.data?.nonce) {
                updateWpRestNonce(data.data.nonce);
                return data.data.nonce;
              }
              return null;
            })
            .catch((err) => {
              console.error("[API] Failed to refresh nonce:", err);
              return null;
            })
            .finally(() => {
              // Clear the lock
              nonceRefreshPromise = null;
            });
        }

        const freshNonce = await nonceRefreshPromise;

        // If we successfully got a new nonce, retry the original request!
        if (freshNonce) {
          console.log(
            "[API] Nonce refreshed successfully. Retrying original request...",
          );
          result = await rawBaseQuery(modifiedArgs, api, extraOptions);

          // Also update the headers in the new response if they exist
          if (result.meta?.response?.headers) {
            const newWpRestNonce =
              result.meta.response.headers.get("X-WP-Nonce");
            if (newWpRestNonce) updateWpRestNonce(newWpRestNonce);
          }
        }
      }
    }
  }
  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: customBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: [
    "Product",
    "Variation",
    "Category",
    "Tag",
    "Brand",
    "Attribute",
    "Cart",
    "SavedForLater",
    "CompareList",
    "Wishlist",
    "Checkout",
    "Order",
    "User",
  ],
  endpoints: () => ({}),
});

export const {
  util: { getRunningQueriesThunk },
  reducer: apiReducer,
  middleware: apiMiddleware,
} = baseApi;